import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';

export default function CodeInput({ id, name, value, length, label, helperText, error, disabled, onChange, onBlur }) {
    const [focused, setFocused] = useState(false);
    const activeIndex = Math.min(value.length, length - 1);
    return <Stack spacing={1.5}>
        <Typography component="label" htmlFor={id} variant="subtitle1" fontWeight={600} textAlign="center">
            {label}
        </Typography>
        <Box sx={{
            position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
            gap: { xs: 0.75, sm: 1 }, width: '100%', maxWidth: 420, alignSelf: 'center'
        }}>
            <Box component="input" id={id} name={name} type="text" value={value}
                onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, length))}
                onPaste={(event) => {
                    const pasted = event.clipboardData.getData('text');
                    if (!pasted) return;
                    event.preventDefault();
                    onChange(pasted.replace(/\D/g, '').slice(0, length));
                }}
                onFocus={() => setFocused(true)} onBlur={(event) => { setFocused(false); onBlur(event); }}
                aria-label={label} aria-invalid={Boolean(error)} aria-describedby={helperText ? id + '-helper' : undefined}
                autoComplete="one-time-code" autoFocus inputMode="numeric" pattern="[0-9]*"
                maxLength={length} disabled={disabled} required
                sx={{
                    position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%',
                    p: 0, border: 0, outline: 0, opacity: 0, cursor: disabled ? 'default' : 'text'
                }} />
            {Array.from({ length }, (_, index) => <Box key={index} aria-hidden="true" className="otp-digit" sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0,
                height: { xs: 52, sm: 64 }, border: '1px solid',
                borderColor: error ? 'error.main' : focused && index === activeIndex ? 'accent.main' : 'divider',
                borderRadius: 2, bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
                color: 'text.primary', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600,
                transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
                ...(focused && index === activeIndex && !disabled && {
                    boxShadow: (theme) => `0 0 0 1px ${error ? theme.palette.error.main : theme.palette.accent.main}`
                })
            }}>{value[index] || ''}</Box>)}
        </Box>
        {helperText && <FormHelperText id={id + '-helper'} error={error} sx={{ textAlign: 'center' }}>{helperText}</FormHelperText>}
    </Stack>;
}
CodeInput.propTypes = {
    id: PropTypes.string.isRequired, name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired, length: PropTypes.number.isRequired, label: PropTypes.string.isRequired,
    helperText: PropTypes.string, error: PropTypes.bool, disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired, onBlur: PropTypes.func.isRequired
};
